import {DatasetDropdown} from "components/basic/dropdowns/dataset/DatasetDropdown";
import {DATE_PATTERN_SECONDS} from "utils/constants";
import {Divider} from "primereact/divider";
import {Dialog} from "primereact/dialog";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {Button} from "primereact/button";
import {InputSwitch, InputSwitchChangeEvent} from "primereact/inputswitch";
import {Tooltip} from "primereact/tooltip";
import React, {useState} from "react";
import {T} from "services/lang/LanguageService";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import {DateTimeFormatter, ZonedDateTime} from "@js-joda/core";
import "./UniversalDatasetTableEditor.scss";
import {defaultHighlightColor} from "./EditBasicCellDialog";
import {ColorKey, ICellConfig, IEditDatasetCellDialogProps, MinMaxValueType} from "../UniversalDatasetTableTypes";
import {useDisplayTimezone} from "hooks/useTimezone";

const tooltipContent = `YYYY - ${T("time.years")}
MM - ${T("time.months")}
DD - ${T("time.days")}
HH - ${T("time.hours")}
mm - ${T("time.minutes")}
ss - ${T("time.seconds")}`;

const EditDatasetCellDialog = ({
  isEditDatasetCellDialogVisible,
  setIsEditDatasetCellDialogVisible,
  selectedDatasetCell,
  updateSelectedDatasetCell,
}: IEditDatasetCellDialogProps) => {
  const [currentConfig, setCurrentConfig] = useState<ICellConfig>(selectedDatasetCell);
  const [minMaxError, setMinMaxError] = useState<string | null>(null);

  const displayTimezone = useDisplayTimezone();

  const saveDatasetCellConfigChanges = () => {
    setIsEditDatasetCellDialogVisible(false);
    updateSelectedDatasetCell(currentConfig);
  };

  const handleEditDatasetCellDialogFooter = () => {
    return (
      <div className="flex flex-row justify-content-end">
        <Button
          label={T("action.cancel")}
          icon="pi pi-times"
          className="component-editor-button icon-red"
          onClick={() => setIsEditDatasetCellDialogVisible(false)}
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          disabled={currentConfig.setMinMaxColors && minMaxError !== null}
          onClick={saveDatasetCellConfigChanges}
          className="component-editor-button icon-green"
        />
      </div>
    );
  };

  const handleTimeValueSwitch = (e: InputSwitchChangeEvent) => {
    setCurrentConfig(prev => ({
      ...prev,
      displayDatasetValueTime: e.value,
      timePattern: e.value ? (prev.timePattern ?? DATE_PATTERN_SECONDS) : undefined,
    }));
  };

  const handleColorChange = (key: ColorKey, value: string) => {
    if (key === ColorKey.COLOR || key === ColorKey.BG_COLOR) {
      setCurrentConfig(prev => ({
        ...prev,
        highlightColor: {
          ...prev.highlightColor,
          [key]: value,
        },
      }));
    }
    setCurrentConfig(prev => ({
      ...prev,
      minMaxColor: {
        ...prev.minMaxColor,
        [key]: value,
      },
    }));
  };

  const getFormattedTime = () => {
    try {
      if (!currentConfig.timePattern) return "";
      const now = ZonedDateTime.now(displayTimezone);
      const formatter = DateTimeFormatter.ofPattern(currentConfig.timePattern);
      return now.format(formatter);
    } catch (error) {
      return "Invalid pattern";
    }
  };

  const validateMinMax = (min?: number, max?: number) => {
    if (min !== undefined && max !== undefined && min >= max) {
      return T("error.invalid-min-max-values");
    }
    return null;
  };

  const handleMinMaxValueChange = (value: number | null, valueType: MinMaxValueType) => {
    const newValue = value ?? undefined;

    if (valueType === MinMaxValueType.MIN) {
      const newMax = currentConfig.minMaxValue?.maxValue;

      setMinMaxError(validateMinMax(newValue, newMax));

      setCurrentConfig(prev => ({
        ...prev,
        minMaxValue: { ...prev.minMaxValue, minValue: newValue },
      }));
    }

    if (valueType === MinMaxValueType.MAX) {
      const newMin = currentConfig.minMaxValue?.minValue;

      setMinMaxError(validateMinMax(newMin, newValue));

      setCurrentConfig(prev => ({
        ...prev,
        minMaxValue: { ...prev.minMaxValue, maxValue: newValue },
      }));
    }
  };

  const handleSetMinMaxColorsChange = (value: boolean) => {
    if (!value) {
      setCurrentConfig(prev => ({
        ...prev,
        minMaxColor: undefined,
        minMaxValue: undefined,
        setMinMaxColors: value,
      }));
    }
    if (minMaxError) setMinMaxError(null);

    setCurrentConfig(prev => ({
      ...prev,
      setMinMaxColors: value,
    }));
  };

  return (
    <Dialog
      header={T("header.edit-dataset-cell")}
      footer={handleEditDatasetCellDialogFooter()}
      visible={isEditDatasetCellDialogVisible}
      onHide={() => setIsEditDatasetCellDialogVisible(false)}
      style={{ width: "500px" }}
    >
      <div className="flex flex-column gap-3 dataset-cell-dialog">
        <DatasetDropdown
          value={currentConfig.value as string}
          onChange={value => setCurrentConfig({ ...currentConfig, value })}
        />
        <div className="flex flex-row justify-content-around">
          <div className="flex flex-column gap-2 align-items-center">
            <label htmlFor="displayDatasetValueTime">{T("label.display-dataset-value-time")}</label>
            <InputSwitch
              checked={currentConfig.displayDatasetValueTime ?? false}
              onChange={handleTimeValueSwitch}
            />
          </div>
          <div className="flex flex-column gap-2 align-items-center">
            <label htmlFor="setMinMaxColors">{T("label.min-max-colors")}</label>
            <InputSwitch
              checked={Boolean(currentConfig.setMinMaxColors ?? false)}
              onChange={e => handleSetMinMaxColorsChange(e.value)}
            />
          </div>
        </div>
        {currentConfig.displayDatasetValueTime && (
          <div className="flex flex-column gap-2">
            <label htmlFor="timePattern" className="tooltip-label">
              {T("label.time-pattern")}
              <i id="timePatternTooltip" className="pi pi-info-circle text-primary ml-2" />
            </label>
            <Tooltip target="#timePatternTooltip" content={tooltipContent} position="top" />
            <InputText
              id="timePattern"
              value={currentConfig.timePattern ?? ""}
              onChange={e => setCurrentConfig({ ...currentConfig, timePattern: e.target.value })}
            />
          </div>
        )}
        <div className="flex flex-row justify-content-around">
          <div className="flex flex-column gap-2 align-items-left">
            <span>{T("word.text-color")}</span>
            <ColorPicker
              value={currentConfig.highlightColor?.color ?? defaultHighlightColor.defaultTextColor}
              onValueChange={e => handleColorChange(ColorKey.COLOR, e.target.value)}
            />
          </div>
          <div className="flex flex-column gap-2 align-items-left">
            <span>{T("word.background-color")}</span>
            <ColorPicker
              value={
                currentConfig.highlightColor?.bgColor ??
                defaultHighlightColor.defaultBackgroundColor
              }
              onValueChange={e => handleColorChange(ColorKey.BG_COLOR, e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-content-center mt-3">
          <span
            className="p-2 preview-box"
            style={{
              color: currentConfig.highlightColor?.color,
              backgroundColor: currentConfig.highlightColor?.bgColor,
            }}
          >
            {currentConfig.displayDatasetValueTime ? getFormattedTime() : T("word.preview")}
          </span>
        </div>
        {currentConfig.setMinMaxColors && (
          <>
            <Divider />
            {minMaxError && <small className="p-error block text-center mt-2">{minMaxError}</small>}
            <div className="flex flex-row justify-content-around">
              <div className="flex flex-column gap-3 align-items-left">
                <span>{T("label.min-value")}</span>
                <InputNumber
                  className="w-10rem"
                  value={currentConfig.minMaxValue?.minValue}
                  onChange={e => handleMinMaxValueChange(e.value, MinMaxValueType.MIN)}
                />
                <div className="flex flex-column gap-2">
                  <label htmlFor="minTextColor">{T("word.text-color")}</label>
                  <ColorPicker
                    value={
                      currentConfig.minMaxColor?.minTextColor ??
                      defaultHighlightColor.defaultTextColor
                    }
                    onValueChange={e => handleColorChange(ColorKey.MIN_TEXT_COLOR, e.target.value)}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="minBgColor">{T("word.background-color")}</label>
                  <ColorPicker
                    value={
                      currentConfig.minMaxColor?.minBgColor ??
                      defaultHighlightColor.defaultBackgroundColor
                    }
                    onValueChange={e => handleColorChange(ColorKey.MIN_BG_COLOR, e.target.value)}
                  />
                </div>
                <span
                  className="p-2 preview-box w-4rem"
                  style={{
                    color: currentConfig.minMaxColor?.minTextColor,
                    backgroundColor: currentConfig.minMaxColor?.minBgColor,
                  }}
                >
                  {T("word.min")}
                </span>
              </div>
              <div className="flex flex-column gap-3 align-items-left">
                <span>{T("label.max-value")}</span>
                <InputNumber
                  className="w-10rem"
                  value={currentConfig.minMaxValue?.maxValue}
                  onChange={e => handleMinMaxValueChange(e.value, MinMaxValueType.MAX)}
                />
                <div className="flex flex-column gap-2">
                  <label htmlFor="maxTextColor">{T("word.text-color")}</label>
                  <ColorPicker
                    value={
                      currentConfig.minMaxColor?.maxTextColor ??
                      defaultHighlightColor.defaultTextColor
                    }
                    onValueChange={e => handleColorChange(ColorKey.MAX_TEXT_COLOR, e.target.value)}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="maxBgColor">{T("word.background-color")}</label>
                  <ColorPicker
                    value={
                      currentConfig.minMaxColor?.maxBgColor ??
                      defaultHighlightColor.defaultBackgroundColor
                    }
                    onValueChange={e => handleColorChange(ColorKey.MAX_BG_COLOR, e.target.value)}
                  />
                </div>
                <span
                  className="p-2 preview-box w-4rem"
                  style={{
                    color: currentConfig.minMaxColor?.maxTextColor,
                    backgroundColor: currentConfig.minMaxColor?.maxBgColor,
                  }}
                >
                  {T("word.max")}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default EditDatasetCellDialog;
